/**
 * The small menu that pops up over what was pressed: a short list beside
 * the thing, with the world still showing and still running round it.
 * Asked for on 2026-10-07 for the log: "right click/long-press in the chat
 * menu should show a context menu, not a full screen menu"; and on
 * 2026-10-08 for the world's people: "clicking an npc when they're within
 * range of interacting, should not move toward them. and use a context
 * menu that pops up over them, like we're supposed to have for message log
 * interactions." Both opened a box in the middle of a darkened screen with
 * a Cancel button under it until then. The bag's icon uses it too, for how
 * the bag is sorted (2026-10-08: "the bag sort by weight should be a
 * context option on the bag panelicon itself, not on the items in the
 * bag.").
 *
 * A long press is always the right-click (see longPress in touch.ts); what
 * a hover would say goes in the menu as its head line, not a choice; and
 * its rows are a finger's size on a touchscreen (style.css).
 *
 * There is one of it, #popmenu, and opening it again moves it. It has no
 * Cancel: a press anywhere else, Escape, or any of its choices shuts it.
 * The press elsewhere is not swallowed, so a click on somebody else in the
 * world shuts this one and opens theirs, and a click on the ground walks.
 * The keyboard goes into it when it opens; the arrows move between its
 * items, Home and End go to the ends, and Enter or Space picks.
 *
 * A choice can carry a picture and a count beside its name, and a line of
 * detail that the row keeps to itself until asked: a hover shows it as the
 * row's tooltip, and a long press or a right-click on the row opens it
 * under the name, and shuts it again. The Equipment panel's places used
 * that (2026-10-09: "show a context menu for inventory slot item selection
 * instead of the fullscreen thing. long pressing on an option gives more
 * details, show just the bare minimum in the menu") until 2026-11-02,
 * when the detail came out from under the hold to stay under each name
 * ("show the details of items in the equipment panel context menu
 * without having to right click or long press"); see PopItem.open.
 *
 * What it lists is plain data (PopMenu), so whoever opens it says what is
 * on it and nothing here knows about NPCs, logs or bags. Where it goes is
 * placePop, which is kept free of the page so it can be run under node.
 */

import { longPress } from "./touch";
import { num } from "./numfmt";

/** One choice on the menu. */
export interface PopItem {
  label: string;
  /** The hover: what choosing it does, or why it is greyed. */
  title?: string;
  disabled?: boolean;
  /**
   * A choice that is one of a set, ticked when it is the one in force:
   * how the bag is sorted. Left out for an ordinary action.
   */
  checked?: boolean;
  /** Drawn in the accent: a quest on an NPC's menu. */
  quest?: boolean;
  /** A picture before the label: an item's sprite. */
  icon?: HTMLElement;
  /** A count written in the picture's corner, as a bag square writes it; only over one. */
  count?: number;
  /**
   * More about the choice than its name: the hover, and the line a long
   * press or a right-click on the row opens under it. Taken as the hover
   * when there is no title.
   */
  detail?: string;
  /**
   * A short line of numbers always shown under the name, unlike detail:
   * the Equipment panel's choices carried their main stats in it until
   * their whole detail took its place (see open).
   */
  stats?: string;
  /**
   * The detail shown under the name from the start, with no hover, hold
   * or right-click to open it: the Equipment panel's choices (2026-11-02:
   * "show the details of items in the equipment panel context menu
   * without having to right click or long press").
   */
  open?: boolean;
  go: () => void;
}

export interface PopMenu {
  /** What the menu is read out as by a screen reader: the thing's name. */
  label: string;
  /** A line over the choices that is not one of them: whom it is about, or the bag's weight. */
  head?: string;
  /**
   * Lines of words under the head, before any choice: what a thing is,
   * for a menu that is only there to say it (a long press on what the
   * Equipment panel holds). Not choices, and not reached by the arrows.
   */
  about?: string[];
  items: PopItem[];
}

/** A box on the screen, in client pixels: what the menu is put beside. */
export interface Anchor {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Where the menu goes: under the anchor, or beside it to the right (for
 * somebody in the world, so the menu does not cover whoever it is about).
 * Near the edge it flips to the other side — over the anchor, or to its
 * left — and if it fits on neither it is pushed back inside the window,
 * which it never leaves while it is smaller than the window.
 */
export type PopSide = "below" | "right";

/** The gap between the anchor and the menu, and the least between the menu and the window's edge. */
const GAP = 4;
const EDGE = 6;

export function placePop(a: Anchor, w: number, h: number, vw: number, vh: number, side: PopSide = "below"): { left: number; top: number } {
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));
  // Somewhere along one axis: after the anchor if there is room, before it
  // if there is room there instead, and otherwise as near after as fits.
  const along = (lo: number, hi: number, size: number, room: number) => {
    if (hi + GAP + size <= room - EDGE) return hi + GAP;
    if (lo - GAP - size >= EDGE) return lo - GAP - size;
    return clamp(hi + GAP, EDGE, Math.max(EDGE, room - EDGE - size));
  };
  // Lined up with the anchor's start across the other axis, pushed in from the far edge.
  const across = (start: number, size: number, room: number) => clamp(start, EDGE, Math.max(EDGE, room - EDGE - size));
  if (side === "right") return { left: along(a.left, a.right, w, vw), top: across(a.top, h, vh) };
  return { left: across(a.left, w, vw), top: along(a.top, a.bottom, h, vh) };
}

/** A point as an anchor: where a press landed, when there is nothing better to put the menu beside. */
export function pointAnchor(x: number, y: number): Anchor {
  return { left: x, top: y, right: x, bottom: y };
}

let el: HTMLElement | null = null;
let back: HTMLElement | null = null;
let closed: (() => void) | null = null;
let on: Element | null = null;
/** Where the menu was put beside, so it can be put again when a row's detail opens. */
let placed: { at: Anchor; side?: PopSide } | null = null;

function menuEl(): HTMLElement {
  if (el) return el;
  el = document.getElementById("popmenu") ?? document.body.appendChild(Object.assign(document.createElement("div"), { id: "popmenu" }));
  el.setAttribute("role", "menu");
  el.tabIndex = -1;
  el.hidden = true;
  wire(el);
  return el;
}

/** Whether the menu is up. */
export function popMenuOpen(): boolean {
  return !!el && !el.hidden;
}

/**
 * Shut the menu. `restore` gives the keyboard back to where it was before
 * it opened (or to the `back` it was opened with): what Escape and a
 * choice do. A press elsewhere does not, since the press is putting the
 * focus where it wants it.
 */
export function hidePopMenu(restore = true) {
  if (!el || el.hidden) return;
  const had = el.contains(document.activeElement);
  el.hidden = true;
  el.replaceChildren();
  const done = closed;
  closed = null;
  if (restore && had && back?.isConnected) back.focus();
  back = null;
  on = null;
  placed = null;
  done?.();
}

/**
 * Open the menu beside `at`. `back` is where the keyboard goes when it
 * shuts, the element focused before it opened when that is left out;
 * `onClose` is told when it shuts, however it shuts. `on` is the element
 * `at` was measured from: only a scroll that moves it shuts the menu.
 * Left out, any scroll does.
 */
export function showPopMenu(
  menu: PopMenu,
  at: Anchor,
  opts: { side?: PopSide; back?: HTMLElement | null; onClose?: () => void; on?: Element } = {},
) {
  const m = menuEl();
  hidePopMenu(false);
  on = opts.on ?? null;
  back = opts.back ?? (document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null);
  closed = opts.onClose ?? null;
  m.setAttribute("aria-label", menu.label);
  const parts: HTMLElement[] = [];
  if (menu.head) {
    const head = document.createElement("div");
    head.className = "pophead";
    head.setAttribute("role", "presentation");
    head.textContent = menu.head;
    parts.push(head);
  }
  for (const line of menu.about ?? []) {
    const p = document.createElement("div");
    p.className = "popabout";
    p.setAttribute("role", "presentation");
    p.textContent = line;
    parts.push(p);
  }
  // A column for the tick when anything on the menu can carry one, so the
  // labels line up whether or not theirs is ticked.
  const ticks = menu.items.some((i) => i.checked !== undefined);
  // And a picture's room on every row when any row has one, likewise.
  const pics = menu.items.some((i) => i.icon);
  for (const it of menu.items) {
    const b = document.createElement("button");
    b.type = "button";
    if (it.checked !== undefined) {
      b.setAttribute("role", "menuitemradio");
      b.setAttribute("aria-checked", String(it.checked));
    } else b.setAttribute("role", "menuitem");
    b.tabIndex = -1;
    if (ticks) {
      const tick = document.createElement("span");
      tick.className = "poptick";
      tick.setAttribute("aria-hidden", "true");
      tick.textContent = it.checked ? "✓" : "";
      b.append(tick);
    }
    if (pics) {
      const pic = document.createElement("span");
      pic.className = "popicon";
      if (it.icon) pic.append(it.icon);
      if (it.count && it.count > 1) {
        const n = document.createElement("span");
        n.className = "count";
        n.textContent = num(it.count);
        pic.append(n);
      }
      b.append(pic);
    }
    const label = document.createElement("span");
    label.className = "poplabel";
    label.append(it.label);
    if (it.stats) {
      const st = document.createElement("span");
      st.className = "popstats";
      st.textContent = it.stats;
      label.append(st);
    }
    b.append(label);
    if (it.title || (it.detail && !it.open)) b.title = it.title ?? it.detail ?? "";
    if (it.detail && it.open) {
      const more = document.createElement("span");
      more.className = "popdetail";
      more.textContent = it.detail;
      label.append(more);
    } else if (it.detail) {
      const more = document.createElement("span");
      more.className = "popdetail";
      more.textContent = it.detail;
      more.hidden = true;
      label.append(more);
      longPress(b, () => {
        more.hidden = !more.hidden;
        b.setAttribute("aria-expanded", String(!more.hidden));
        b.focus({ preventScroll: true });
        place();
      });
    }
    if (it.quest) b.classList.add("quest");
    b.disabled = !!it.disabled;
    b.addEventListener("click", () => {
      // Shut first, so a choice that moves the keyboard (Reply, into the
      // chat box) has the last word on where it goes.
      hidePopMenu(true);
      it.go();
    });
    parts.push(b);
  }
  m.replaceChildren(...parts);
  m.hidden = false;
  placed = { at, side: opts.side };
  place();
  (items()[0] ?? m).focus({ preventScroll: true });
}

/** Put the menu where it fits beside what it was opened on: measured where it cannot be seen first. */
function place() {
  if (!el || el.hidden || !placed) return;
  el.style.left = "0px";
  el.style.top = "0px";
  el.style.visibility = "hidden";
  const r = el.getBoundingClientRect();
  const { left, top } = placePop(placed.at, r.width, r.height, window.innerWidth, window.innerHeight, placed.side);
  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;
  el.style.visibility = "";
}

/** The choices that can be picked, in order. */
function items(): HTMLButtonElement[] {
  return el ? Array.from(el.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")) : [];
}

function wire(m: HTMLElement) {
  // Anywhere else pressed shuts it, and the press goes on to do what it
  // does. In the capture phase, so nothing that stops the press on its way
  // down can keep the menu up.
  document.addEventListener(
    "pointerdown",
    (ev) => {
      if (!m.hidden && !(ev.target instanceof Node && m.contains(ev.target))) hidePopMenu(false);
    },
    true,
  );
  // The window changing size, or something under the menu scrolling, moves
  // what it was put beside: it shuts rather than point at the wrong thing.
  // Only a scroll of something the anchor is in, though: the log scrolls
  // itself whenever a line comes in, and that shut an NPC's menu a moment
  // after it opened (2026-10-08: "the context menu on npcs seems to close
  // after a short while without interaction").
  window.addEventListener("resize", () => hidePopMenu(false));
  document.addEventListener(
    "scroll",
    (ev) => {
      if (m.hidden || !(ev.target instanceof Node) || m.contains(ev.target)) return;
      if (on && !ev.target.contains(on)) return;
      hidePopMenu(false);
    },
    true,
  );
  // No browser menu over this one.
  m.addEventListener("contextmenu", (ev) => ev.preventDefault());
  // The keys, ahead of the game's own (main.ts listens on the window, and
  // would take an arrow for a step and Enter for the chat box). Only the
  // keys the menu answers are kept from it; anything else still plays.
  document.addEventListener(
    "keydown",
    (ev) => {
      if (m.hidden) return;
      const inside = m.contains(document.activeElement);
      if (ev.key === "Escape") {
        hidePopMenu(true);
      } else if (!inside) {
        return;
      } else if (ev.key === "ArrowDown" || ev.key === "ArrowUp" || ev.key === "Home" || ev.key === "End") {
        const list = items();
        if (list.length) {
          const i = list.indexOf(document.activeElement as HTMLButtonElement);
          const n = list.length;
          const j = ev.key === "Home" ? 0 : ev.key === "End" ? n - 1 : ev.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n;
          list[j].focus({ preventScroll: true });
          // Into sight in a menu too long for the window, which scrolls.
          list[j].scrollIntoView({ block: "nearest" });
        }
      } else if (ev.key === "Enter" || ev.key === " ") {
        const b = document.activeElement;
        if (b instanceof HTMLButtonElement && m.contains(b) && !b.disabled) b.click();
      } else if (ev.key === "Tab") {
        hidePopMenu(true);
      } else if (ev.key === "ArrowLeft" || ev.key === "ArrowRight") {
        // Nothing to the side in a list; kept from walking all the same.
      } else return;
      ev.preventDefault();
      ev.stopPropagation();
    },
    true,
  );
}
